import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import TopBar from "@/components/TopBar";
import BottomNav from "@/components/BottomNav";
import CategoryBadge from "@/components/CategoryBadge";
import { MapPin, Wallet, Calendar, Search, SlidersHorizontal, X, ArrowUpDown, Rocket } from "lucide-react";
import { toast } from "sonner";
import { useMeta } from "@/lib/meta";

const STATUSES = ["pending", "accepted", "rejected"];
const SORTS = [
  { value: "newest", labelKey: "sortNewest" },
  { value: "oldest", labelKey: "sortOldest" },
  { value: "price_asc", labelKey: "sortPriceAsc" },
  { value: "price_desc", labelKey: "sortPriceDesc" },
];

const colorByStatus = {
  pending: "bg-amber-50 text-amber-700",
  accepted: "bg-emerald-50 text-emerald-700",
  rejected: "bg-zinc-100 text-zinc-500",
};

export default function MerchantOffers() {
  const { t, tCat, tCity } = useLang();
  const { meta } = useMeta();
  const [offers, setOffers] = useState([]);
  const [err, setErr] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ q: "", status: "", min_price: "", max_price: "", sort: "newest" });

  const load = async () => {
    setErr("");
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v != null) params[k] = v; });
      const r = await api.get("/offers/mine", { params });
      setOffers(r.data);
    } catch (e) { setErr(errMsg(e)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.status, filters.min_price, filters.max_price, filters.sort]);

  useEffect(() => {
    const id = setTimeout(load, 250);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.q]);

  const activeCount = useMemo(
    () => Object.entries(filters).filter(([k, v]) => !["q", "sort"].includes(k) && v).length,
    [filters]
  );

  const reset = () => setFilters({ q: "", status: "", min_price: "", max_price: "", sort: "newest" });

  return (
    <div className="app-shell pb-24" data-testid="merchant-offers">
      <TopBar title={t("myOffers")} back={false} />
      <div className="px-5 pt-4 space-y-3">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute start-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              data-testid="offers-search"
              placeholder={t("searchRequest")}
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              className="w-full h-11 rounded-full border border-zinc-200 bg-white ps-9 pe-4 outline-none focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 text-sm"
            />
          </div>
          <button
            data-testid="offers-toggle-filters"
            onClick={() => setShowFilters((s) => !s)}
            className={`relative h-11 px-4 rounded-full inline-flex items-center gap-2 text-sm font-medium transition ${
              showFilters || activeCount > 0 ? "bg-emerald-600 text-white" : "bg-white border border-zinc-200 text-zinc-700"
            }`}
          >
            <SlidersHorizontal className="h-4 w-4" />
            {activeCount > 0 && <span className="text-xs">{activeCount}</span>}
          </button>
        </div>

        {showFilters && (
          <div className="card-surface p-4 space-y-3" data-testid="offers-filters-panel">
            <div>
              <div className="label-caps mb-2">{t("status")}</div>
              <div className="flex flex-wrap gap-2">
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    data-testid={`offers-filter-status-${s}`}
                    onClick={() => setFilters({ ...filters, status: filters.status === s ? "" : s })}
                    className={`pill border transition ${filters.status === s ? "bg-emerald-600 text-white border-emerald-600" : "bg-white border-zinc-200 text-zinc-700"}`}
                  >
                    {t(s)}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="label-caps mb-1.5">{t("minPrice")}</div>
                <input
                  data-testid="offers-min-price"
                  type="number"
                  min="0"
                  value={filters.min_price}
                  onChange={(e) => setFilters({ ...filters, min_price: e.target.value })}
                  className="w-full h-11 rounded-xl border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-emerald-600 outline-none px-3 text-sm"
                />
              </div>
              <div>
                <div className="label-caps mb-1.5">{t("maxPrice")}</div>
                <input
                  data-testid="offers-max-price"
                  type="number"
                  min="0"
                  value={filters.max_price}
                  onChange={(e) => setFilters({ ...filters, max_price: e.target.value })}
                  className="w-full h-11 rounded-xl border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-emerald-600 outline-none px-3 text-sm"
                />
              </div>
            </div>
            <div>
              <div className="label-caps mb-2 inline-flex items-center gap-1"><ArrowUpDown className="h-3 w-3" /> {t("sort")}</div>
              <div className="flex flex-wrap gap-2">
                {SORTS.map((s) => (
                  <button
                    key={s.value}
                    data-testid={`offers-sort-${s.value}`}
                    onClick={() => setFilters({ ...filters, sort: s.value })}
                    className={`pill border transition ${filters.sort === s.value ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-200 text-zinc-700"}`}
                  >
                    {t(s.labelKey)}
                  </button>
                ))}
              </div>
            </div>
            {activeCount > 0 && (
              <button data-testid="offers-reset-filters" onClick={reset} className="text-sm text-emerald-700 font-semibold inline-flex items-center gap-1">
                <X className="h-3 w-3" /> {t("reset")}
              </button>
            )}
          </div>
        )}

        {err && <div className="text-sm text-red-600 bg-red-50 rounded-lg p-3">{err}</div>}
        {offers.length === 0 && !err && <div className="card-surface p-5 text-sm text-zinc-600">{t("noOffers")}</div>}

        <div className="space-y-3">
          {offers.map((o) => (
            <div
              key={o.id}
              data-testid={`my-offer-${o.id}`}
              className="card-surface p-4"
            >
              <Link to={`/merchant/request/${o.request_id}`} className="flex gap-3 items-start">
                <CategoryBadge category={o.request?.category || "other"} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-zinc-900 truncate">{tCat(o.request?.category || "other")}</div>
                    <span className={`pill ${colorByStatus[o.status]}`}>{t(o.status)}</span>
                  </div>
                  <p className="text-sm text-zinc-600 line-clamp-1 mt-0.5">{o.request?.description}</p>
                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-zinc-500">
                    <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{tCity(o.request?.city)}</span>
                    <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold"><Wallet className="h-3 w-3" />{o.price} {t("saudiRiyal")}</span>
                    <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{o.delivery_time}</span>
                  </div>
                </div>
              </Link>
              {o.status === "pending" && meta?.payments_enabled && (
                o.boosted ? (
                  <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 px-3 py-1 text-xs font-semibold" data-testid={`boosted-${o.id}`}>
                    <Rocket className="h-3 w-3" /> {t("featured")}
                  </div>
                ) : (
                  <button
                    data-testid={`boost-${o.id}`}
                    onClick={async (e) => {
                      e.preventDefault();
                      try {
                        const { data } = await api.post(`/offers/${o.id}/boost`);
                        toast.success(`Boosted! ${data.credits_remaining} credits left`);
                        load();
                      } catch (err) {
                        if (err?.response?.status === 402) {
                          toast.error("Out of credits — buy more first");
                        } else {
                          toast.error(errMsg(err));
                        }
                      }
                    }}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 hover:bg-emerald-100 px-3 py-1.5 text-xs font-semibold transition"
                  >
                    <Rocket className="h-3 w-3" /> {t("boostThisOffer")}
                  </button>
                )
              )}
            </div>
          ))}
        </div>
      </div>
      <BottomNav />
    </div>
  );
}
